// Quote Builder — formatters & helpers.

const GBP_FMT = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Money math hub: convert a GBP number (possibly fractional pounds) to integer pence. */
export function pencify(gbp: number): number {
  if (!Number.isFinite(gbp)) return 0;
  return Math.round(gbp * 100);
}

/** Render integer pence as a GBP string (£1,234.56). */
export function fmtPence(pence: number): string {
  return GBP_FMT.format(pence / 100);
}

/** Render a fractional GBP number as a GBP string. Prefer `fmtPence` for sums. */
export function fmtGbp(gbp: number): string {
  return GBP_FMT.format(gbp);
}

export function escapeHtml(s: unknown): string {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Relative "Xs/m/h/d ago" formatter. */
export function fmtAgo(ts: number): string {
  if (!ts) return "";
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/** ID generator — `<prefix><base36-time><base36-rand>`. */
export function newId(prefix: string): string {
  return prefix + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36);
}

/**
 * Generate a temporary client-side KOS-Q reference.
 *
 * Format: KOS-Q-XXXX. The canonical reference comes from the server on first
 * save (legacy `_qbv3SendCustomer` reads it from the response). This is a
 * placeholder used only until the first server round-trip.
 */
export function tempQuoteRef(): string {
  return "KOS-Q-" + Date.now().toString(36).slice(-4).toUpperCase();
}

/** Inline SVG icon — mirrors source's `svg(d)` helper at kos-qb-v3.js:1177.
 *  Source uses `|` as a multi-subpath separator. 24×24 viewBox, 1.5px stroke,
 *  rounded caps — matches the platform design system (`feedback_design_icons`). */
export function svgIcon(paths: string): string {
  const list = paths.split("|").map((d) => `<path d="${d}"/>`).join("");
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${list}</svg>`;
}

/** Canonical icon paths used across the QB pickers. Verbatim from source. */
export const ICONS = {
  plus: "M12 5v14|M5 12h14",
  save: "M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z|M17 21v-8H7v8|M7 3v5h8",
  message: "M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z",
} as const;
